import { pickFromPool } from '@puzzlemania/shared';

/**
 * Module 64 (WP-64.A2/A4) — level→music-track service.
 *
 * The music counterpart of `levelBackground.ts`, and deliberately its twin: the
 * composition root owns the data, the game asks a question, and a game with no
 * pool gets `null` and keeps its own behaviour. Nothing here plays anything —
 * it decides an id, exactly as the background service decides CSS.
 *
 * Three inputs, each already existing for its own reasons:
 *   - the pool     ← the plugin manifest's `audioManifest.tracks`, whose ORDER
 *                    is the rotation order (divergence 5)
 *   - exclusions   ← `virtual:audio-exclusions` (Module 56), so a track excluded
 *                    in the Audio Studio leaves the rotation without any second
 *                    registry to keep in sync
 *   - the stride   ← `musicStride` in `audio/assignments/<game>.json` (decision 3)
 *
 * That is why Module 64 adds no pool file: every part of a pool already had a
 * home.
 */

/** What the service needs to know about one game. */
export interface MusicPoolSource {
  /** Ordered music ids — the manifest's `audioManifest.tracks`. */
  readonly tracks: readonly string[];
  /** Levels one track holds for. `1` rotates every level (background parity). */
  readonly stride?: number;
}

export type GetLevelMusic = (game: string, level: number) => string | null;

/**
 * How many preceding tracks may not repeat (Module 64, WP-64.A7).
 *
 * **One**, not the scheduler's default of `min(5, P-1)`, and this is the one
 * place Module 64 deliberately does NOT copy the background system.
 *
 * The default window has a property that never shows on a background pool of
 * nine but is unavoidable on a music pool of three or four: when the window
 * forbids everything except one candidate — which it does for every pool of six
 * or fewer — the shuffled cycle degenerates into a FIXED cycle. A three-track
 * pool would play 1,2,3,1,2,3 for all 120 levels: never the previous track,
 * perfectly even, perfectly stable on reload, and not in any sense random.
 *
 * A window of 1 keeps all three of those guarantees — each cycle is still a
 * full permutation, so distribution stays even within ±1 — while leaving real
 * choice inside the cycle. It is the smallest window that still means "never
 * the track you just heard", which is exactly what was asked for.
 */
export const MUSIC_REPEAT_WINDOW = 1;

/**
 * Levels are 1-based and the scheduler is 0-based, so the whole of the stride
 * is this one expression. Exported for the tests that pin the boundary: with
 * a stride of 5, levels 1–5 are index 0 and 6–10 are index 1. Block Jam ships
 * `stride: 1`, so the index is simply `level - 1` and the track changes with
 * every board, exactly as the background image does.
 *
 * `stride` is clamped rather than trusted. `floor(i / 0)` is `Infinity` and
 * `pickFromPool` would then clamp every level to index 0 — one track for the
 * whole run, which looks like "the rotation silently stopped working" rather
 * than like a bad config value. The vite plugin already refuses a bad stride at
 * build time; this is the second rail, because the service is also constructed
 * directly in tests and by any future caller.
 */
export function musicIndexForLevel(level: number, stride = 1): number {
  const lvl = Math.max(1, Math.floor(level));
  const s = Number.isInteger(stride) && stride >= 1 ? stride : 1;
  return Math.floor((lvl - 1) / s);
}

/**
 * Build the callback the composition root injects as `IGameContext.getLevelMusic`.
 *
 * Pure w.r.t. its inputs, like `createLevelBackgroundService`: the real
 * manifests and the real exclusion list are passed in by the caller, so this
 * module has no build-time coupling and is unit-testable with fabricated pools.
 *
 * Returns `null` — meaning "you decide" — when the game is unknown, when every
 * track is excluded, or when the pool is empty. It never throws: a game left
 * with no music is a quieter failure than a game that will not start.
 */
export function createLevelMusicService(
  sources: Record<string, MusicPoolSource>,
  excludedIds: readonly string[] = [],
): GetLevelMusic {
  const excluded = new Set(excludedIds);
  return (game, level) => {
    const source = sources[game];
    if (!source) return null;
    // Filtered here rather than at construction so that the exclusion list and
    // the manifest can be supplied independently, in either order.
    const pool = source.tracks.filter((id) => !excluded.has(id));
    if (pool.length === 0) return null;
    return pickFromPool(pool, musicIndexForLevel(level, source.stride), MUSIC_REPEAT_WINDOW);
  };
}
